import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export type AnalyticsRange = '1d' | '7d' | '30d' | '90d';

export interface AnalyticsOverview {
  totalUsers: number;
  activeUsers: number;
  newRegistrations: number;
  loginAttempts: number;
  loginSuccessRate: number;
  pageViews: number;
  topPages: { page: string; views: number }[];
  registrationFunnel: {
    pageViews: number;
    formStarted: number;
    formSubmitted: number;
    registrationSuccess: number;
    conversionRate: number;
  };
}

export interface ActivityTrendPoint {
  date: string;
  pageViews: number;
  uniqueUsers: number;
  registrations: number;
  logins: number;
}

export interface PageClicks {
  topPages: { name: string; clicks: number; uniqueUsers: number; clicksPerUser: number }[];
  eventDistribution: { id: string; name: string; value: number; category: string; page: string; uniqueUsers: number }[];
  heatmapData: { date: string; hour: number; clicks: number; uniqueUsers: number }[];
  trackClicks: { name: string; clicks: number; uniqueUsers: number; clicksPerUser: number }[];
  summary: { totalClicks: number; totalPages: number };
}

export interface RealtimeBar {
  user_identifier: string;
  user_type: 'authenticated' | 'anonymous';
  user_display_name: string | null;
  minute: string;
  activity_count: number;
  color: string;
}

export interface RealtimeActivity {
  chartData: RealtimeBar[];
  summary: { activeUsers: number; totalActivity: number; timeRange: string; lastUpdate: string; isFullDay: boolean; date: string | null };
}

export interface UserActivityDetail {
  userInfo: { identifier: string; displayName: string | null; userType: 'authenticated' | 'anonymous'; isAggregated: boolean };
  activities: {
    event_type: string;
    page_type: string | null;
    track_name: string | null;
    race_number: number | null;
    selected_date: string | null;
    metadata: unknown;
    timestamp: string | null;
    user_type: 'authenticated' | 'anonymous';
  }[];
  summary: {
    totalActivities: number;
    uniqueEventTypes: number;
    uniquePages: number;
    firstActivity: string | null;
    lastActivity: string | null;
    timeRange: string;
  };
  breakdown: { eventTypes: { type: string; count: number }[]; pageTypes: { type: string | null; count: number }[] };
}

export function useAnalyticsOverview(range: AnalyticsRange) {
  return useQuery({
    queryKey: ['analytics', 'overview', range],
    queryFn: () => apiClient<AnalyticsOverview>(`/analytics/overview?range=${range}`),
  });
}

export function useActivityTrend(range: AnalyticsRange) {
  return useQuery({
    queryKey: ['analytics', 'activity', range],
    queryFn: () => apiClient<ActivityTrendPoint[]>(`/analytics/activity?range=${range}`),
  });
}

export function usePageClicks(range: AnalyticsRange) {
  return useQuery({
    queryKey: ['analytics', 'page-clicks', range],
    queryFn: () => apiClient<PageClicks>(`/analytics/page-clicks?range=${range}`),
  });
}

/** Live per-user activity. `date` (YYYY-MM-DD) → full-day view; otherwise the last `timeframe` minutes. Auto-refreshes every 30s unless paused. */
export function useRealtimeActivity(params: { timeframe: string; date: string | null; autoRefresh: boolean }) {
  const query = params.date ? `date=${params.date}` : `timeframe=${params.timeframe}`;
  return useQuery({
    queryKey: ['analytics', 'realtime', params.date, params.timeframe],
    queryFn: () => apiClient<RealtimeActivity>(`/analytics/realtime?${query}`),
    refetchInterval: params.autoRefresh && !params.date ? 30_000 : false,
  });
}

export function useUserActivityDetail(userIdentifier: string | null, params: { timeframe: string; date: string | null }) {
  const query = params.date ? `date=${params.date}` : `timeframe=${params.timeframe}`;
  return useQuery({
    queryKey: ['analytics', 'user-activity', userIdentifier, params.date, params.timeframe],
    queryFn: () => apiClient<UserActivityDetail>(`/analytics/user-activity/${encodeURIComponent(userIdentifier as string)}?${query}`),
    enabled: !!userIdentifier,
  });
}
